import { createFileRoute } from "@tanstack/react-router";
import Breadcrumbs from "src/components/base/breadcrumb";
import { Heading } from "src/components/base/heading";
import { Divider } from "src/components/base/divider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { ApiInternal } from "src/api/api_internal";
import { handleFormError } from "src/utils/form-error";
import Form from "src/components/base/form";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { Dialog } from "src/components/base/dialog";
import { notify } from "src/components/base/toast";
import { ClipboardIcon } from "@heroicons/react/20/solid";
import { Combobox, ComboboxOption } from "src/components/base/combobox";

/** Form to create a user invitation */
function RouteComponent() {
    const [t] = useTranslation("dialog-create-invitation");
    const [tg] = useTranslation();

    const {
        settings: { public_origin },
        tenants: { list: tenants },
    } = Route.useLoaderData();

    const [tenantQuery, setTenantQuery] = useState("");
    const [inviteLink, setInviteLink] = React.useState<string | undefined>(undefined);

    const form = useForm({
        defaultValues: {
            tenant: tenants.length === 1 ? tenants[0] : null,
            access_start: "",
            cant_login_after: "",
            display_name: "",
            link_valid_until: 1,
            mail: "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (value.access_start.length !== 0 && value.cant_login_after.length !== 0) {
                    if (new Date(value.access_start) > new Date(value.cant_login_after)) {
                        return {
                            fields: {
                                access_start: t("error.access-start-cant-be-after-cant-login-after"),
                                cant_login_after: t("error.access-start-cant-be-after-cant-login-after"),
                            },
                        };
                    }
                }
                if (value.tenant === null) return { fields: { tenant: t("error.missing-tenant") } };

                const res = await ApiInternal.users.invite({
                    tenant_uuid: value.tenant.uuid,
                    access_start: value.access_start.length !== 0 ? value.access_start + ":00.0Z" : undefined,
                    cant_login_after:
                        value.cant_login_after.length !== 0 ? value.cant_login_after + ":00.0Z" : undefined,
                    display_name: value.display_name,
                    link_valid_until: value.link_valid_until,
                    mail: value.mail,
                });

                if (res.result === "Err") {
                    return handleFormError(res.error, {
                        has_local: (errors) => {
                            errors.fields.mail = t("error.already_accepted_link");
                        },
                        has_oidc: (errors) => {
                            errors.fields.mail = t("error.already_oidc");
                        },
                        has_invited: (errors) => {
                            errors.fields.mail = t("error.already_invited");
                        },
                        start_after_until: (errors) => {
                            errors.fields.access_start = t("error.start-after-until");
                        },
                    });
                }

                const inviteLink = public_origin + "/s/invite/" + res.value.uuid;
                setInviteLink(inviteLink);
            },
        },
    });

    return (
        <div className={"flex flex-col gap-6"}>
            <div className={"flex flex-col gap-3"}>
                <Breadcrumbs items={[{ label: t("button.users"), to: "/a/users" }, { label: t("label.invite") }]} />
                <Heading>{t("heading.user-invite")}</Heading>
                <Divider />
            </div>

            <Form onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field name={"tenant"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.tenant")}</RequiredLabel>
                                    <Description>{t("description.tenant")}</Description>
                                    <Combobox
                                        required
                                        autoFocus={tenants.length !== 1}
                                        value={fieldApi.state.value}
                                        onChange={(x) => fieldApi.handleChange(x)}
                                        onClose={() => setTenantQuery("")}
                                        queryDisplay={(x) => x?.name ?? ""}
                                        onQueryChange={(q) => setTenantQuery(q)}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                    >
                                        {tenants
                                            .filter((x) => x.name.toLowerCase().includes(tenantQuery.toLowerCase()))
                                            .map((x) => (
                                                <ComboboxOption key={x.uuid} value={x}>
                                                    <Label>{x.name}</Label>
                                                </ComboboxOption>
                                            ))}
                                    </Combobox>

                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"display_name"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.display_name")}</RequiredLabel>
                                    <Description>{t("description.display_name")}</Description>
                                    <Input
                                        autoFocus={tenants.length === 1}
                                        required={true}
                                        maxLength={255}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"mail"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.mail")}</RequiredLabel>
                                    <Description>{t("description.mail")}</Description>
                                    <Input
                                        type={"email"}
                                        maxLength={255}
                                        required={true}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>

                        <form.Field
                            name={"access_start"}
                            validators={{
                                onChangeListenTo: ["cant_login_after"],
                                onChange: ({ fieldApi, value }) => {
                                    const cant_login_after = fieldApi.form.getFieldValue("cant_login_after");
                                    if (value.length !== 0 && cant_login_after.length !== 0) {
                                        if (new Date(value) > new Date(cant_login_after)) {
                                            return t("error.access-start-cant-be-after-cant-login-after");
                                        }
                                    }
                                    return null;
                                },
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.access-start")}</Label>
                                    <Description>{t("description.access-start")}</Description>
                                    <Input
                                        type={"datetime-local"}
                                        lang={"de-De"}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage key={err}>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>

                        <form.Field
                            name={"cant_login_after"}
                            validators={{
                                onChangeListenTo: ["access_start"],
                                onChange: ({ fieldApi, value }) => {
                                    const access_start = fieldApi.form.getFieldValue("access_start");
                                    if (access_start.length !== 0 && value.length !== 0) {
                                        if (new Date(access_start) > new Date(value)) {
                                            return t("error.access-start-cant-be-after-cant-login-after");
                                        }
                                    }
                                    return null;
                                },
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.cant_login_after")}</Label>
                                    <Description>{t("description.cant_login_after")}</Description>
                                    <Input
                                        type={"datetime-local"}
                                        lang={"de-De"}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage key={err}>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"link_valid_until"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.link_valid_until")}</RequiredLabel>
                                    <Description>{t("description.link_valid_until")}</Description>
                                    <Input
                                        type={"number"}
                                        required={true}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(Number(e.target.value))}
                                        min={1}
                                        max={255}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <div>
                            <PrimaryButton type={"submit"}>{t("button.invite")}</PrimaryButton>
                        </div>
                    </FieldGroup>
                </Fieldset>
            </Form>

            {inviteLink && (
                <Dialog open={true} onClose={() => setInviteLink(undefined)}>
                    <Fieldset>
                        <FieldGroup>
                            <Field>
                                <Label>{t("label.invite-link")}</Label>
                                <span className={"mt-3 flex gap-3"}>
                                    <Input value={inviteLink} contentEditable={false} readOnly={true} />
                                    <Button
                                        plain={true}
                                        onClick={async () => {
                                            await navigator.clipboard.writeText(inviteLink);
                                            notify.success(tg("toast.copied-to-clipboard"));
                                            setInviteLink(undefined);
                                        }}
                                    >
                                        <ClipboardIcon />
                                    </Button>
                                </span>
                            </Field>
                        </FieldGroup>
                    </Fieldset>
                </Dialog>
            )}
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/users/invite")({
    component: RouteComponent,
    loader: async () => {
        const [settings, tenants] = await Promise.all([
            ApiInternal.settings.getGeneral(),
            ApiInternal.tenants.getAll(),
        ]);
        return { settings, tenants };
    },
});
